import { Money } from "../../../components/ui/Money";
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale';
import React, { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, Plus } from 'lucide-react';
import { EntityActionMenu } from '../../../components/ui/EntityActionMenu';
import {
 useRecoveryEntries,
 useCreateRecoveryEntry,
 useInitiateWriteOff,
 useApproveWriteOff
} from '../hooks';
import { getSession } from '../../../lib/session';
import { fetcher } from '../../../lib/api-fetcher';
import Decimal from 'decimal.js';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/FormField';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { useToast } from '../../../components/ui/Toast';
import { defineDetailPage, useDetailPage } from '@repo/detail';
import { DetailPage } from '@repo/web-detail';

const detailConfig = defineDetailPage({
  resource: 'defaults',
  fetchById: async (id: string) => {
    const res = await fetcher.get<any>(`/staff/v1/defaults/${id}`);
    return res && typeof res === 'object' && 'data' in res ? res.data : res;
  },
  tabs: [],
});

export function DefaultRecordDetail({ recordId }: { recordId: string }) {
 const detail = useDetailPage(detailConfig, recordId);
 const { data: entries, isLoading: entriesLoading } = useRecoveryEntries(recordId);
 const approveWriteOffMutation = useApproveWriteOff(recordId);
 const { toast } = useToast();

 const [showAddRecovery, setShowAddRecovery] = useState(false);
 const [showWriteOff, setShowWriteOff] = useState(false);

 const defaultRecord = detail.data;
 const currentUserId = getSession()?.user?.id;

 const actions: Array<{ actionType: string; performedBy: string; performedAt: string }> = defaultRecord?.actions ?? [];
 const latestWriteOffAction = [...actions].reverse().find((a) => a.actionType === 'WRITE_OFF');
 const isWriteOffPendingApproval = defaultRecord?.status !== 'written_off' && !!latestWriteOffAction;
 const canApproveWriteOff = isWriteOffPendingApproval && latestWriteOffAction?.performedBy !== currentUserId;

 const totalOutstanding = new Decimal(defaultRecord?.outstandingPrincipal ?? 0).plus(defaultRecord?.outstandingPenalty ?? 0);
 const totalRecovered = entries?.reduce((sum, entry) => sum.plus(entry.amountPaise), new Decimal(0)) || new Decimal(0);
 const remainingBalance = totalOutstanding.minus(totalRecovered);
 const recoveryPercent = totalOutstanding.isZero() ? 0 : totalRecovered.dividedBy(totalOutstanding).times(100).toNumber();

 const handleApproveWriteOff = () => {
   approveWriteOffMutation.mutate(undefined, {
     onSuccess: () => {
       toast({ type: 'success', title: 'Write-off approved', description: 'The remaining balance has been written off as bad debt.' });
     },
     onError: (err: unknown) => {
       toast({ type: 'error', title: 'Failed to approve write-off', description: (err as { message?: string })?.message || 'Please try again.' });
     },
   });
 };

 return (
 <DetailPage
   config={detailConfig}
   detail={{ ...detail, isLoading: detail.isLoading || entriesLoading }}
   title={defaultRecord?.customerName ?? 'Default Record'}
   backButton={<Link to="/defaults" className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors w-fit"><ChevronLeft className="h-4 w-4" />Back</Link>}
   headerActions={
     <div className="flex items-center gap-2">
       {defaultRecord?.status !== 'written_off' && (
         <Button variant="primary" size="sm" onClick={() => setShowAddRecovery(true)}>
           <Plus className="w-4 h-4 mr-1" />
           Add Recovery Entry
         </Button>
       )}
       <EntityActionMenu
         actions={[
           {
             label: 'Initiate Write-off',
             variant: 'destructive',
             requiresConfirmation: true,
             confirmTitle: 'Initiate Bad Debt Write-off',
             confirmDescription: 'Are you sure you want to initiate write-off for this defaulted account? A second approval will be required.',
             hidden: defaultRecord?.status === 'written_off' || isWriteOffPendingApproval,
             onClick: () => setShowWriteOff(true),
           },
           {
             label: 'Approve Write-off',
             variant: 'destructive',
             requiresConfirmation: true,
             confirmTitle: 'Approve Write-off',
             confirmDescription: 'Are you sure you want to approve writing off the remaining balance as bad debt?',
             hidden: !canApproveWriteOff,
             onClick: handleApproveWriteOff,
           },
         ]}
       />
     </div>
   }
   mainPane={
     <div className="space-y-6">
 <Card className="flex justify-between items-start">
 <CardContent className="pt-6">
 <div className="flex items-center space-x-2">
  <h1 className="text-2xl font-bold">{defaultRecord?.customerName ?? 'N/A'}</h1>
  <span className="px-2 py-1 bg-info-bg text-info-text text-xs rounded-full">{defaultRecord?.groupId ?? 'N/A'}</span>
  <span className={`px-2 py-1 text-xs rounded-full ${
  defaultRecord?.status === 'active' ? 'bg-danger-bg text-danger-text' :
  defaultRecord?.status === 'partial' ? 'bg-warning-bg text-warning-text' :
  defaultRecord?.status === 'resolved' ? 'bg-success-bg text-success-text' : 'bg-surface-2 text-text-secondary'
  }`}>
  {defaultRecord?.status ?? 'unknown'}
 </span>
 </div>
  <div className="mt-2 text-sm text-text-secondary">
  <p>Default Date: {defaultRecord?.defaultDate ?? 'N/A'}</p>
 <p>Total Outstanding: <Money paise={totalOutstanding.toNumber()} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></p>
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardHeader><CardTitle>Recovery Progress</CardTitle></CardHeader>
 <CardContent>
  <div className="w-full bg-surface-2 rounded-full h-4 mb-2">
 <div className="bg-accent-500 h-4 rounded-full" style={{ width: `${Math.min(recoveryPercent, 100)}%` }}></div>
 </div>
 <div className="flex justify-between text-sm">
 <span>Recovered: <Money paise={totalRecovered.toNumber()} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></span>
 <span className="font-bold text-danger-text">Remaining: <Money paise={remainingBalance.toNumber()} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></span>
 </div>
 </CardContent>
 </Card>

 <div className="flex space-x-4">
 <Button
 variant="primary"
 onClick={() => setShowAddRecovery(true)}
 >
 Add Recovery Entry
 </Button>
  {defaultRecord?.status !== 'written_off' && !isWriteOffPendingApproval && (
 <Button
 variant="danger"
 onClick={() => setShowWriteOff(true)}
 >
 Initiate Write-off
 </Button>
 )}
  {canApproveWriteOff && (
 <Button
 variant="danger"
 loading={approveWriteOffMutation.isPending}
 onClick={handleApproveWriteOff}
 >
 Approve Write-off
 </Button>
 )}
  {isWriteOffPendingApproval && !canApproveWriteOff && (
 <span className="text-xs text-text-muted self-center">Write-off pending a second approver's review.</span>
 )}
 </div>

 <Card className="overflow-hidden">
 <CardHeader className="border-b">
 <CardTitle>Recovery History</CardTitle>
 </CardHeader>
  <table className="min-w-full divide-y divide-border">
  <thead className="bg-surface-2">
  <tr>
  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Date</th>
  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Amount</th>
  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Paid By</th>
  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Notes</th>
  </tr>
  </thead>
  <tbody className="bg-surface-1 divide-y divide-border">
 {entries?.map(entry => (
 <tr key={entry.id}>
 <td className="px-6 py-4 whitespace-nowrap">{new Date(entry.recoveredAt).toLocaleDateString()}</td>
 <td className="px-6 py-4 whitespace-nowrap"><Money paise={entry.amountPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></td>
 <td className="px-6 py-4 whitespace-nowrap">{entry.paidBy ?? '—'}</td>
 <td className="px-6 py-4 whitespace-nowrap">{entry.notes ?? '—'}</td>
 </tr>
 ))}
 {entries?.length === 0 && (
 <tr>
  <td colSpan={4} className="px-6 py-4 text-center text-text-secondary">No recovery entries found.</td>
 </tr>
 )}
 </tbody>
 </table>
 </Card>

 {showAddRecovery && (
 <AddRecoveryModal 
 recordId={recordId} 
 onClose={() => setShowAddRecovery(false)} 
 />
 )}

 {showWriteOff && (
 <WriteOffDangerModal 
 recordId={recordId} 
 onClose={() => setShowWriteOff(false)} 
 />
 )}
 </div>
   }
 />
 );
}

function AddRecoveryModal({ recordId, onClose }: { recordId: string, onClose: () => void }) {
 const { mutate, isPending } = useCreateRecoveryEntry(recordId);
 const [amountStr, setAmountStr] = useState('');
 const [paidBy, setPaidBy] = useState('');
 const [notes, setNotes] = useState('');

 const handleSubmit = (e: React.FormEvent) => {
 e.preventDefault();
 const amountPaise = new Decimal(amountStr).times(100).toNumber();
 mutate({ amountPaise, paidBy: paidBy || undefined, notes: notes || undefined }, {
 onSuccess: () => {
 onClose();
 }
 });
 };

 return (
 <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-modal">
  <Card className="max-w-md w-full">
    <CardHeader><CardTitle>Record Recovery</CardTitle></CardHeader>
 <CardContent>
 <form onSubmit={handleSubmit} className="space-y-4">
 <div>
 <label className="block text-sm font-medium mb-1">Amount</label>
 <Input 
 type="number" 
 step="0.01"
 value={amountStr} 
 onChange={e => setAmountStr(e.target.value)}
 required
 />
 </div>
 <div>
 <label className="block text-sm font-medium mb-1">Paid By</label>
 <Input 
 type="text" 
 value={paidBy} 
 onChange={e => setPaidBy(e.target.value)}
 placeholder="e.g., Member, Surety"
 required
 />
 </div>
 <div>
 <label className="block text-sm font-medium mb-1">Notes</label>
 <textarea 
 value={notes} 
 onChange={e => setNotes(e.target.value)}
 className="w-full border rounded-md p-2"
 rows={3}
 />
 </div>
 <div className="flex justify-end space-x-2 pt-4">
 <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
 <Button type="submit" variant="primary" disabled={isPending}>
 {isPending ? 'Saving...' : 'Record Recovery'}
 </Button>
 </div>
 </form>
 </CardContent>
 </Card>
 </div>
 );
}

function WriteOffDangerModal({ recordId, onClose }: { recordId: string, onClose: () => void }) {
 const { mutate, isPending } = useInitiateWriteOff(recordId);
 const [confirmText, setConfirmText] = useState('');
 const [reason, setReason] = useState('');
 const { toast } = useToast();

 const handleSubmit = (e: React.FormEvent) => {
 e.preventDefault();
 if (confirmText !== 'WRITE OFF' || !reason.trim()) return;
 mutate(reason.trim(), {
 onSuccess: () => {
 toast({ type: 'success', title: 'Submitted for approval', description: 'The write-off will apply once a second user approves it.' });
 onClose();
 },
 onError: (err: unknown) => {
 toast({ type: 'error', title: 'Failed to initiate write-off', description: (err as { message?: string })?.message || 'Please try again.' });
 },
 });
 };

 return (
 <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-modal">
  <Card className="max-w-md w-full">
    <CardHeader><CardTitle className="text-danger-text">Initiate Write-Off</CardTitle></CardHeader>
 <CardContent>
 <div className="bg-danger-bg text-danger-text p-3 rounded-md mb-4 text-sm">
 Warning: this submits the remaining balance for write-off as bad debt. A different user must approve it before it takes effect.
 </div>
 <form onSubmit={handleSubmit} className="space-y-4">
 <div>
 <label className="block text-sm font-medium mb-1">Reason</label>
 <textarea
 value={reason}
 onChange={e => setReason(e.target.value)}
 className="w-full border rounded-md p-2"
 rows={3}
 required
 />
 </div>
 <div>
 <label className="block text-sm font-medium mb-1">Type 'WRITE OFF' to confirm</label>
 <Input
 type="text"
 value={confirmText}
 onChange={e => setConfirmText(e.target.value)}
 required
 />
 </div>
 <div className="flex justify-end space-x-2 pt-4">
 <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
 <Button
 type="submit"
 variant="danger"
 disabled={isPending || confirmText !== 'WRITE OFF' || !reason.trim()}
 >
 {isPending ? 'Submitting...' : 'Submit for Approval'}
 </Button>
 </div>
 </form>
 </CardContent>
 </Card>
 </div>
 );
}
